/**
 * خلاصه تحلیل تکنیکال
 * =====================
 * همان کاری که یک تحلیل‌گر در ده ثانیه اول می‌کند: چند اندیکاتور کلیدی را
 * نگاه می‌کند و می‌گوید «فعلاً سمتِ خرید است یا فروش».
 *
 * ⚠ این **توصیه معاملاتی نیست** و در رابط کاربری هم همین نوشته می‌شود.
 * جمعِ چند اندیکاتور، آینده را نمی‌گوید؛ فقط وضعیت فعلی را خلاصه می‌کند.
 *
 * ── چرا دو گروه جدا و نه یک عدد ────────────────────────────────────────
 * نوسان‌گرها (RSI، استوکاستیک، CCI، …) و میانگین‌های متحرک ذاتاً دو حرف
 * متفاوت می‌زنند: در یک روند صعودیِ قوی، همه میانگین‌ها می‌گویند «خرید» و
 * همه نوسان‌گرها می‌گویند «اشباع خرید». اگر این دو را در یک عدد قاطی
 * می‌کردیم، خروجی در قوی‌ترین روندها **بی‌معنا** می‌شد — دقیقاً همان اشتباهی
 * که یک بار در پیاده‌سازی اول کردیم و با داده آزمون لو رفت.
 *
 * پس سه خلاصه داده می‌شود: میانگین‌ها، نوسان‌گرها، و مجموع.
 */
import {
  Candle, bollinger, cci, ema, macd, rsi, sma, stoch, williamsR,
} from './indicators'

export type Vote = -1 | 0 | 1
export type Group = 'ma' | 'osc'
export type SignalRow = { key: string; label: string; value: string; vote: Vote; group: Group }
export type Score = {
  buy: number
  sell: number
  neutral: number
  score: number            // بین ۱− و ۱+
  label: string
}
export type Summary = {
  rows: SignalRow[]
  ma: Score
  osc: Score
  all: Score
}

const last = (a: (number | null)[]): number | null => {
  for (let i = a.length - 1; i >= 0; i--) if (a[i] != null) return a[i] as number
  return null
}

const fx = (v: number | null, d = 2) => (v == null ? '—' : v.toFixed(d))

const MA_PERIODS = [10, 20, 50, 100, 200]

export function technicalSummary(candles: Candle[]): Summary | null {
  if (!candles || candles.length < 30) return null
  const closes = candles.map((c) => c.close)
  const price = closes[closes.length - 1]
  const rows: SignalRow[] = []
  const push = (group: Group, key: string, label: string, value: string, vote: Vote) =>
    rows.push({ key, label, value, vote, group })

  /* ── میانگین‌های متحرک: قیمت بالای میانگین یعنی خرید */
  for (const p of MA_PERIODS) {
    if (closes.length < p) continue
    const v = last(sma(closes, p))
    if (v != null) {
      push('ma', `sma${p}`, `میانگین ساده ${p}`, fmtPrice(v), price > v ? 1 : price < v ? -1 : 0)
    }
    const e = last(ema(closes, p))
    if (e != null) {
      push('ma', `ema${p}`, `میانگین نمایی ${p}`, fmtPrice(e), price > e ? 1 : price < e ? -1 : 0)
    }
  }

  /* ── نوسان‌گرها */
  const r = last(rsi(closes, 14))
  if (r != null) push('osc', 'rsi', 'RSI (۱۴)', fx(r), r >= 70 ? -1 : r <= 30 ? 1 : 0)

  const m = macd(closes, 12, 26, 9)
  const h = last(m.hist)
  if (h != null) push('osc', 'macd', 'MACD (۱۲/۲۶/۹)', fx(h, 4), h > 0 ? 1 : h < 0 ? -1 : 0)

  const st = stoch(candles, 14, 3, 3)
  const k = last(st.k)
  if (k != null) push('osc', 'stoch', 'استوکاستیک ٪K', fx(k), k >= 80 ? -1 : k <= 20 ? 1 : 0)

  const cc = last(cci(candles, 20))
  if (cc != null) push('osc', 'cci', 'CCI (۲۰)', fx(cc, 1), cc >= 100 ? -1 : cc <= -100 ? 1 : 0)

  const wr = last(williamsR(candles, 14))
  if (wr != null) push('osc', 'wr', 'ویلیامز ٪R', fx(wr, 1), wr >= -20 ? -1 : wr <= -80 ? 1 : 0)

  const bb = bollinger(closes, 20, 2)
  const bu = last(bb.upper)
  const bl = last(bb.lower)
  if (bu != null && bl != null) {
    push('osc', 'bb', 'باند بولینگر',
      price >= bu ? 'سقف باند' : price <= bl ? 'کف باند' : 'میانه',
      price >= bu ? -1 : price <= bl ? 1 : 0)
  }

  if (!rows.length) return null
  return {
    rows,
    ma: score(rows.filter((x) => x.group === 'ma')),
    osc: score(rows.filter((x) => x.group === 'osc')),
    all: score(rows),
  }
}

function score(rows: SignalRow[]): Score {
  const buy = rows.filter((x) => x.vote === 1).length
  const sell = rows.filter((x) => x.vote === -1).length
  const neutral = rows.length - buy - sell
  const s = rows.length ? (buy - sell) / rows.length : 0
  const label = s >= 0.5 ? 'خرید قوی'
    : s >= 0.2 ? 'خرید'
      : s <= -0.5 ? 'فروش قوی'
        : s <= -0.2 ? 'فروش' : 'خنثی'
  return { buy, sell, neutral, score: s, label }
}

function fmtPrice(v: number): string {
  const abs = Math.abs(v)
  return v.toLocaleString('en-US', { maximumFractionDigits: abs >= 1000 ? 0 : abs >= 1 ? 2 : 6 })
}
